import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Send, Loader2, X, MessageSquare } from 'lucide-react';
import api from '@/lib/api';
import { motion, AnimatePresence } from 'framer-motion';

export default function ArchitectChat({ context, onClose, inline = false }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => crypto.randomUUID());
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (inputRef.current && !inline) {
      inputRef.current.focus();
    }
  }, [inline]);

  const contextLabel = context?.type === 'feasibility' ? 'Feasibility Report'
    : context?.type === 'cost' ? 'Cost Reality Report'
    : context?.type === 'fit_score' ? 'Fit Score Results'
    : null;

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;

    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setInput('');
    setLoading(true);

    try {
      const payload = { message: text, session_id: sessionId };
      if (context && messages.length === 0) {
        payload.context = context;
      }
      const { data } = await api.post('/architect-chat', payload);
      setMessages(prev => [...prev, { role: 'assistant', content: data.reply }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Unable to respond at the moment. Your results remain valid.' }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const chatContent = (
    <div className={`flex flex-col ${inline ? 'h-[500px]' : 'h-full'}`} data-testid="architect-chat">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-black/10 flex-shrink-0">
        <div>
          <h3 className="font-heading text-sm font-medium text-brand-dark">Ask an Architect</h3>
          <p className="text-xs text-brand-dark/40 font-body">Architectural guidance — not design</p>
        </div>
        {onClose && (
          <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0" data-testid="chat-close">
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>

      {/* Context banner */}
      {contextLabel && (
        <div className="px-5 py-2.5 bg-brand-light border-b border-black/5 flex-shrink-0">
          <p className="text-xs text-brand-dark/50 font-body">
            Context: <span className="font-medium text-brand-dark/70">{contextLabel}</span> — ask about your results
          </p>
        </div>
      )}

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-4" data-testid="chat-messages">
        {messages.length === 0 && (
          <div className="text-center py-8">
            <MessageSquare className="w-8 h-8 text-brand-dark/10 mx-auto mb-3" />
            <p className="text-sm text-brand-dark/40 font-body max-w-xs mx-auto leading-relaxed">
              {context
                ? 'Ask about your results — what they mean, what influenced them, and what to consider next.'
                : 'Ask about architecture in South Africa — budgets, processes, feasibility, and what to expect.'}
            </p>
            <div className="mt-4 flex flex-wrap gap-2 justify-center">
              {context?.type === 'feasibility' && (
                <>
                  <SuggestedQ text="Why is my feasibility rated this way?" onClick={setInput} />
                  <SuggestedQ text="What could improve my score?" onClick={setInput} />
                </>
              )}
              {context?.type === 'cost' && (
                <>
                  <SuggestedQ text="What's included in architecture fees?" onClick={setInput} />
                  <SuggestedQ text="Where do cost surprises typically come from?" onClick={setInput} />
                </>
              )}
              {context?.type === 'fit_score' && (
                <>
                  <SuggestedQ text="What does this score mean for my project?" onClick={setInput} />
                  <SuggestedQ text="How can I improve my readiness?" onClick={setInput} />
                </>
              )}
              {!context && (
                <>
                  <SuggestedQ text="Renovation vs new build — how should I decide?" onClick={setInput} />
                  <SuggestedQ text="Why does architecture take time?" onClick={setInput} />
                </>
              )}
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`} data-testid={`chat-msg-${i}`}>
            <div className={`max-w-[85%] rounded-sm px-4 py-3 ${
              msg.role === 'user'
                ? 'bg-brand-dark text-white'
                : 'bg-brand-light border border-black/5 text-brand-dark'
            }`}>
              <div className={`text-sm font-body leading-relaxed whitespace-pre-wrap ${msg.role === 'user' ? 'text-white/90' : 'text-brand-dark/80'}`}>
                {msg.content}
              </div>
            </div>
          </div>
        ))}

        {loading && (
          <div className="flex justify-start" data-testid="chat-loading">
            <div className="bg-brand-light border border-black/5 rounded-sm px-4 py-3">
              <div className="flex items-center gap-2 text-brand-dark/40">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                <span className="text-xs font-body">Considering your question...</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="px-5 py-3 border-t border-black/10 flex-shrink-0">
        <div className="flex gap-2">
          <Textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask about architecture..."
            className="rounded-sm min-h-[40px] max-h-[100px] resize-none text-sm"
            rows={1}
            data-testid="chat-input"
          />
          <Button
            onClick={sendMessage}
            disabled={!input.trim() || loading}
            className="bg-brand-dark text-white hover:bg-gray-800 rounded-sm self-end h-10 w-10 p-0 flex-shrink-0"
            data-testid="chat-send"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
        <p className="text-[10px] text-brand-dark/25 font-body mt-1.5">AI interprets. Architects decide. Responses are educational, not professional advice.</p>
      </div>
    </div>
  );

  if (inline) {
    return (
      <div className="bg-white rounded-sm border border-black/10 overflow-hidden">
        {chatContent}
      </div>
    );
  }

  // Overlay mode
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4 md:p-8"
        onClick={(e) => { if (e.target === e.currentTarget && onClose) onClose(); }}
        data-testid="chat-overlay"
      >
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.2 }}
          className="bg-white rounded-sm w-full max-w-lg h-[600px] shadow-2xl overflow-hidden"
        >
          {chatContent}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function SuggestedQ({ text, onClick }) {
  return (
    <button
      onClick={() => onClick(text)}
      className="px-3 py-1.5 text-xs font-body text-brand-dark/50 border border-black/10 rounded-sm hover:border-brand-accent/30 hover:text-brand-dark/70 transition-colors"
      data-testid="suggested-question"
    >
      {text}
    </button>
  );
}
